/*
 OK FBI — Cinematic Final Reveal Overlay

 Polls /api/reveal/state?case_id=... every 2s while mounted.
 When reveal.active is true, renders a cinematic fullscreen overlay
 corresponding to reveal.step (0..9). OBS-safe (no OWNER controls, no admin
 data, only case-facing content).

 The OWNER drives step progression via /api/owner/reveal/trigger.
*/
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/pages/AuthPage";
import { assetUrl } from "@/contexts/SessionContext";
import { ShieldCheck, Clock, Fingerprint, User, FileText, Radio, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 2000;

export default function CinematicRevealOverlay({ caseId }) {
  const [state, setState] = useState({ active: false, step: -1 });
  const [content, setContent] = useState({});
  const [caseData, setCaseData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem(`reveal_dismissed_${caseId}`));
    } catch {
      return false;
    }
  });
  const [prevStep, setPrevStep] = useState(null);
  const { isOwner, user } = useAuth();

  const dismiss = () => {
    try {
      if (caseId) sessionStorage.setItem(`reveal_dismissed_${caseId}`, "1");
    } catch {}
    setDismissed(true);
  };

  // Load case data (needed to render lineup/evidence in the reveal)
  useEffect(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then(({ data }) => setCaseData(data)).catch(() => {});
  }, [caseId]);

  // Poll reveal state
  useEffect(() => {
    if (!caseId) return;
    setState({ active: false, step: -1 });
    setContent({});
    try {
      setDismissed(Boolean(sessionStorage.getItem(`reveal_dismissed_${caseId}`)));
    } catch {
      setDismissed(false);
    }
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/reveal/state`, { params: { case_id: caseId } });
        if (!alive) return;
        const rev = data.reveal || { active: false, step: -1 };
        setState(rev);
        setContent(data.content || {});
        // If a new live step is actively pushed from host (step advances), re-open for viewers
        if (rev.active && rev.step >= 0 && prevStep !== null && rev.step !== prevStep) {
          try { sessionStorage.removeItem(`reveal_dismissed_${caseId}`); } catch {}
          setDismissed(false);
        }
        setPrevStep(rev.active ? rev.step : null);
      } catch { /* ignore */ }
    };
    tick();
    const int = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(int); };
  }, [caseId]);

  // Escape key to dismiss overlay
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [caseId]);

  // Audio ducking while overlay is active and visible
  useEffect(() => {
    if (state.active && !dismissed && state.step >= 0) {
      try { window.dispatchEvent(new CustomEvent("okfbi:duck:start")); } catch {}
    } else {
      try { window.dispatchEvent(new CustomEvent("okfbi:duck:end")); } catch {}
    }
  }, [state.active, dismissed, state.step]);

  const step = state?.step ?? -1;

  if (!state.active || step < 0 || !caseData || dismissed || state.expired) return null;

  const handleHostClose = async () => {
    try {
      await api.post("/owner/reveal/trigger", { case_id: caseId, step: -1 });
      setState({ active: false, step: -1 });
      dismiss();
      toast.success("تم إنهاء العرض وإغلاقه للجميع");
    } catch {
      toast.error("فشل إنهاء العرض");
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="cinematic-reveal"
        className="fixed inset-0 z-[80] bg-black/98 backdrop-blur-xl overflow-y-auto"
        dir="rtl"
      >
        <div className="absolute inset-0 pointer-events-none opacity-40"
             style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(217,166,80,0.18), transparent 60%)" }} />
        <div className="relative min-h-screen flex flex-col items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-5xl">
            {/* Top Control Bar */}
            <div className="flex items-center justify-between w-full mb-4 px-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                <span className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">OK · FBI · FINAL REVEAL</span>
              </div>
              <div className="flex items-center gap-2">
                {(isOwner || user?.role === "OWNER" || user?.account_type === "OWNER") && (
                  <button
                    type="button"
                    onClick={handleHostClose}
                    data-testid="reveal-host-close-btn"
                    className="px-3 py-1.5 bg-red-950/80 hover:bg-red-900 border border-red-800 text-red-300 rounded text-xs font-cairo flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <RotateCcw size={13} />
                    <span>إنهاء العرض للجميع (Host Reset)</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="reveal-dismiss-btn"
                  className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-gold/40 text-zinc-300 hover:text-white rounded text-xs font-cairo flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <X size={14} className="text-gold" />
                  <span>إغلاق العرض (ESC)</span>
                </button>
              </div>
            </div>

            <div className="text-center mb-6">
              <div className="font-cairo text-white text-2xl md:text-4xl mt-2" data-testid="reveal-step-title">
                {state.step_title || "…"}
              </div>
              <div className="text-xs text-zinc-500 font-cairo mt-2">
                القضية: {caseData.title}
              </div>
            </div>

            <div className="border border-gold/40 bg-black/70 backdrop-blur p-6 md:p-8">
              {step === 0 && <StepLock caseData={caseData} />}
              {step === 1 && <StepDossier caseData={caseData} content={content} />}
              {step === 2 && <StepLineup caseData={caseData} />}
              {step === 3 && <StepEvidence items={content.critical_evidence || []} />}
              {step === 4 && <StepTimeline caseData={caseData} />}
              {step === 5 && <StepContradictions content={content} />}
              {step === 6 && <StepMotive content={content} caseData={caseData} />}
              {step === 7 && <StepCulprit culprit={content.culprit} caseData={caseData} />}
              {step === 8 && <StepExplanation content={content} />}
              {step === 9 && <StepLeaderboard caseId={caseId} />}
            </div>

            {/* Step progress ticker */}
            <div className="flex items-center gap-1 justify-center mt-6 text-[10px] font-mono-ar text-zinc-600">
              {Array.from({ length: 10 }).map((_, i) => (
                <span key={i}
                  className={`h-0.5 w-8 ${i <= step ? "bg-gold" : "bg-zinc-800"}`} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function StepLock() {
  return (
    <div className="text-center py-10">
      <ShieldCheck className="mx-auto text-gold mb-4" size={48} />
      <div className="text-white font-cairo text-xl mb-2">تم قفل التصويت</div>
      <div className="text-sm text-zinc-400 font-cairo">لا يمكن للمحققين تعديل نظرياتهم بعد الآن.</div>
    </div>
  );
}

function StepDossier({ caseData, content }) {
  const summary = content?.summary || caseData.summary || caseData.description;
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-3 flex items-center gap-2">
        <FileText size={18} /> الملف السرّي
      </div>
      <div className="text-sm text-zinc-200 font-cairo leading-relaxed whitespace-pre-line">
        {summary}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-xs text-zinc-400 font-cairo">
        <Fact label="المكان" value={caseData.location} />
        <Fact label="التاريخ" value={caseData.date} />
        <Fact label="الوقت" value={caseData.time} />
        <Fact label="عدد المشتبه بهم" value={(caseData.suspects || []).length} />
      </div>
    </div>
  );
}
function Fact({ label, value }) {
  return (
    <div className="border border-metal p-3">
      <div className="text-[10px] tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-white font-cairo text-sm">{value || "-"}</div>
    </div>
  );
}

function StepLineup({ caseData }) {
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-4">لاصق المشتبه بهم</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(caseData.suspects || []).map((s) => (
          <motion.div key={s.id}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * (parseInt(s.id.replace(/[^0-9]/g, "")) || 0) }}
            className="border border-metal bg-black/60">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src={assetUrl(s.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"}
                alt={s.name || ""}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }}
              />
            </div>
            <div className="p-2">
              <div className="text-white text-sm font-cairo">{s.name}</div>
              <div className="text-[10px] text-zinc-500 font-cairo">{s.job}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function StepEvidence({ items }) {
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-4 flex items-center gap-2">
        <Fingerprint size={18} /> الأدلة الحاسمة
      </div>
      {items.length === 0 ? (
        <div className="text-zinc-500 font-cairo text-sm">لم يُحدَّد أدلة حاسمة لهذه القضية.</div>
      ) : (
        <div className="grid gap-2">
          {items.map((e, i) => (
            <motion.div key={e.id}
              initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 * i }}
              className="border border-gold/40 bg-yellow-950/20 p-3">
              <div className="text-white font-cairo text-sm">{e.name}</div>
              {e.description && <div className="text-[11px] text-zinc-400 font-cairo mt-1">{e.description}</div>}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepTimeline({ caseData }) {
  const events = caseData.timeline || [];
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-4 flex items-center gap-2">
        <Clock size={18} /> الخط الزمني
      </div>
      <div className="relative pr-4 border-r-2 border-gold/40 space-y-3">
        {events.map((ev, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * i }}
            className="relative">
            <span className="absolute -right-[22px] top-1 w-3 h-3 rounded-full bg-gold" />
            <div className="text-gold text-xs font-mono-ar">{ev.time}</div>
            <div className="text-white text-sm font-cairo">{ev.event || ev.desc || ev.description}</div>
          </motion.div>
        ))}
        {events.length === 0 && (
          <div className="text-zinc-500 font-cairo text-sm">لا يوجد خط زمني مسجّل.</div>
        )}
      </div>
    </div>
  );
}

function StepContradictions({ content }) {
  const contradiction = content?.contradiction;
  const alibi = content?.culprit_alibi;

  if (!contradiction && !alibi) {
    return (
      <div>
        <div className="text-gold font-cairo text-lg mb-4">التناقضات</div>
        <div className="text-sm text-zinc-300 font-cairo leading-relaxed">لا توجد تناقضات مسجّلة.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-4">التناقضات</div>
      <div className="text-sm text-zinc-300 font-cairo leading-relaxed space-y-2">
        {alibi && (
          <div className="text-zinc-400">
            ادعاء حجة الغياب: <span className="text-white">"{alibi}"</span>
          </div>
        )}
        {contradiction && (
          <div className="text-zinc-200">
            {contradiction}
          </div>
        )}
      </div>
    </div>
  );
}

function StepMotive({ content, caseData }) {
  let motive = content?.motive || content?.culprit?.motive;
  if (!motive && content?.culprit?.id && caseData?.suspects) {
    const s = caseData.suspects.find(x => x.id === content.culprit.id);
    motive = s?.motive;
  }
  if (!motive && caseData?.solution?.motive) {
    motive = caseData.solution.motive;
  }
  if (!motive && caseData?.motive) {
    motive = caseData.motive;
  }
  return (
    <div className="text-center py-6" data-testid="reveal-motive-step">
      <div className="text-gold font-cairo text-lg mb-4">الدافع</div>
      <div className="text-white font-cairo text-xl" data-testid="reveal-motive-text">{motive || "غير معلوم"}</div>
    </div>
  );
}

function StepCulprit({ culprit, caseData }) {
  let c = culprit;
  if (typeof c === "string" && caseData?.suspects) {
    c = caseData.suspects.find(s => s.id === culprit || s.name === culprit) || { id: culprit, name: culprit };
  } else if (c && caseData?.suspects && (!c.name || c.name === c.id)) {
    const found = caseData.suspects.find(s => s.id === c.id);
    if (found) c = { ...found, ...c, name: found.name };
  }
  if (!c && caseData?.solution?.culprit_id && caseData?.suspects) {
    c = caseData.suspects.find(s => s.id === caseData.solution.culprit_id);
  }
  if (!c && caseData?.culprit_id && caseData?.suspects) {
    c = caseData.suspects.find(s => s.id === caseData.culprit_id);
  }
  if (!c || (!c.name && !c.id)) return (<div className="text-zinc-400 font-cairo">حل القضية غير مسجّل.</div>);
  const displayName = c.name || c.id;
  return (
    <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.7 }}
      className="text-center py-4"
      data-testid="reveal-culprit-card">
      <div className="text-[10px] tracking-[0.5em] text-gold font-mono-ar mb-4">CULPRIT IDENTIFIED</div>
      <div className="mx-auto w-40 h-52 md:w-56 md:h-72 border-2 border-gold overflow-hidden bg-zinc-900"
           style={{ boxShadow: "0 0 60px rgba(217,166,80,0.35)" }}>
        <img
          src={assetUrl(c?.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"}
          alt={displayName}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }}
        />
      </div>
      <div className="mt-5 text-white font-cairo text-3xl" data-testid="reveal-culprit-name">{displayName}</div>
      <div className="text-gold text-sm font-mono-ar mt-1">{c.job ? `${c.job} · ` : ""}{c.id}</div>
    </motion.div>
  );
}

function StepExplanation({ content }) {
  const explanation = content?.explanation || "لا يوجد شرح مكتوب لهذه القضية.";
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-3">الشرح النهائي</div>
      <div className="text-sm text-zinc-200 font-cairo leading-relaxed whitespace-pre-line">
        {explanation}
      </div>
    </div>
  );
}

function StepLeaderboard({ caseId }) {
  const { isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!isOwner) return;
    api.get(`/theories/${caseId}`)
      .then(({ data }) => setRows((data || [])
        .filter((t) => (t.score_pct ?? 0) > 0)
        .sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0))
        .slice(0, 10)))
      .catch(() => setRows([]));
  }, [caseId, isOwner]);
  return (
    <div>
      <div className="text-gold font-cairo text-lg mb-4 flex items-center gap-2">
        <User size={18} /> أفضل المحققين
      </div>
      {rows.length === 0 ? (
        <div className="text-zinc-500 font-cairo text-sm">لا توجد نتائج بعد.</div>
      ) : (
        <div className="grid gap-2">
          {rows.map((t, i) => (
            <div key={t.theory_id} className="flex items-center justify-between border border-metal p-2.5">
              <div className="flex items-center gap-3">
                <div className="text-gold font-mono-ar text-sm w-6">{i + 1}</div>
                <div className="text-gold font-mono-ar text-sm">#{t.agent_id}</div>
              </div>
              <div className="text-white font-cairo text-sm">{t.score_pct}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
